import { ReactNode } from 'react'
import type { SvgIconComponent } from '@mui/icons-material'
import Close from '@mui/icons-material/Close'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import {
  Chip,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material'
import { deepOrange } from '@mui/material/colors'
import { getMaxDecimals } from '@/utils/decimal'
import { showSuccessSnackbar } from '@/utils/snackbars'
import copyToClipboard from '../document-actions-menu/copy-to-clipboard'
import { formatValue } from './shared'

type DataGroupNodeProps = {
  title: string
  icon: SvgIconComponent
  children: ReactNode
  /** null renders the total row with a dash (value missing); undefined hides the row entirely (the project has no amount field configured). */
  amountTotal: string | null | undefined
  poNumber?: string | null
  headerColor?: string
  onClose?: () => void
  /** Shows a warning icon next to the PO# chip with this tooltip when the PO number doesn't match the invoice. */
  poNumberDiscrepancyTooltip?: string
}

export default function DataGroupNode({
  title,
  icon: Icon,
  children,
  amountTotal,
  poNumber,
  headerColor = 'primary.main',
  onClose,
  poNumberDiscrepancyTooltip,
}: DataGroupNodeProps) {
  function handleCopyPoNumber() {
    if (!poNumber) return
    copyToClipboard(poNumber)
    showSuccessSnackbar('PO number copied to clipboard')
  }

  const amountTotalDecimals = amountTotal
    ? Math.max(2, getMaxDecimals([amountTotal]))
    : 0

  // Deep orange reads well on the warning-orange PO banner but looks red on
  // other banner colors (e.g. the invoice card's blue), where amber fits
  // better — 800 because lighter shades wash out against the banner.
  const poDiscrepancyIconColor =
    headerColor === 'warning.main' ? deepOrange[900] : deepOrange[600]

  return (
    <Card
      elevation={4}
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
        borderRadius: 3,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          bgcolor: headerColor,
          transition: 'background-color 0.2s',
          color: 'primary.contrastText',
          px: 3,
          ...(onClose && { pr: 1.5 }),
          py: 1,
          // Fixed height so close button doesn't make PO card taller than invoice card
          minHeight: 46,
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Icon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {poNumber && (
            <Tooltip title="Click to copy PO number">
              <Chip
                label={
                  <Stack direction="row" alignItems="center">
                    {poNumberDiscrepancyTooltip && (
                      <Tooltip
                        title={poNumberDiscrepancyTooltip}
                        disableInteractive
                      >
                        <WarningAmberRounded
                          data-testid="po-number-discrepancy-icon"
                          sx={{
                            fontSize: 16,
                            color: poDiscrepancyIconColor,
                            mr: 0.5,
                          }}
                        />
                      </Tooltip>
                    )}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        opacity: 0.6,
                        mr: 0.75,
                      }}
                    >
                      PO#
                    </Typography>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {poNumber}
                    </Typography>
                  </Stack>
                }
                onClick={handleCopyPoNumber}
                size="small"
                sx={{
                  background: 'transparent',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                }}
              />
            </Tooltip>
          )}
          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: 'primary.contrastText',
                // This makes the hover more visible on the colored background
                '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.15)' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <CardContent sx={{ p: 2 }}>
        {children}
        {amountTotal !== undefined && (
          <>
            <Divider />
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontFamily: 'monospace' }}
              >
                {formatValue(amountTotal, {
                  decimals: amountTotalDecimals,
                  prefix: '$',
                })}
              </Typography>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  )
}
